import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateDirective, TranslateLoader, TranslatePipe, provideTranslateService } from '@ngx-translate/core';
import { SeAnalyticsModule } from 'se-analytics';
import { SeFeAlertModule } from 'se-fe-alert';
import { SeFeApiModule } from 'se-fe-api';
import { SeFeButtonModule } from 'se-fe-button';
import { SeFeCardModule } from 'se-fe-card';
import { SeFeChipModule, SeFeFilterChipsModule } from 'se-fe-chip';
import { SeFeConfirmModule } from 'se-fe-confirm';
import { SeFeDatepickerModule } from 'se-fe-datepicker';
import { SeFeEmptyStateModule } from 'se-fe-empty-state';
import { SeFeFormControlHelpModule } from 'se-fe-form-control-help';
import { SeFeFormFieldModule } from 'se-fe-form-field';
import { SeFeFormFieldOptionsModule } from 'se-fe-form-field-options';
import { SeFeFormFieldSuffixModule } from 'se-fe-form-field-suffix';
import { SeFeFormFieldTextModule } from 'se-fe-form-field-text';
import { SeFeFrameRouterModule, SeFeFrameRouterRedirectService } from 'se-fe-frame-router';
import { SeFeGridModule } from 'se-fe-grid';
import { SeFeHeaderModule } from 'se-fe-header';
import {
  SeFeIconModule,
  seFeIconArchive,
  seFeIconChevronLeft,
  seFeIconChevronRight,
  seFeIconClipboardThin,
  seFeIconDiscountThin,
  seFeIconDotFill,
  seFeIconPencilFill,
  seFeIconOpenInNew,
  seFeIconPlusCircle,
  seFeIconPriceTagThin,
  seFeIconRemove,
  seFeIconSearch,
  seFeIconSearchThin,
  seFeIconSpotClipboard,
  seFeIconSpotDiscount,
  seFeIconTrash,
  seFeIconUnarchive,
  seFeIconWarning,
} from 'se-fe-icon';
import { SeFeMenuModule } from 'se-fe-menu';
import { SeFeModalModule } from 'se-fe-modal';
import { SeFePageModule } from 'se-fe-page';
import { SeFePaginationModule } from 'se-fe-pagination';
import { SeFePositionModule } from 'se-fe-position';
import { SeFeProgressBarModule } from 'se-fe-progress-bar';
import { SeFeScrollModule } from 'se-fe-scroll';
import { SeFeServiceModule } from 'se-fe-service';
import { SeFeSpinnerModule } from 'se-fe-spinner';
import { SeFeTableModule } from 'se-fe-table';
import { SeFeTestIdModule } from 'se-fe-test-id';
import { SeFeTabGroupModule } from 'se-fe-tabs';
import { SeFeToastModule } from 'se-fe-toast';
import { SeFeToolbarModule } from 'se-fe-toolbar';
import { SeFeTooltipModule } from 'se-fe-tooltip';
import {
  SE_FE_TRANSLATIONS_BASE_PATH,
  SE_FE_TRANSLATIONS_LANGUAGES,
  SE_FE_TRANSLATIONS_PREFIX,
  SeFeTranslationsLoader,
} from 'se-fe-translations';
import { environment } from '../environments/environment';
import { AppRedirectService } from './app-redirect.service';
import { AppComponent } from './app.component';
import { AddDiscountComponent } from './discounts/add-discount/add-discount.component';
import { CustomerBuysComponent } from './discounts/customer-buys/customer-buys.component';
import { CustomerGetsComponent } from './discounts/customer-gets/customer-gets.component';
import { DiscountDetailComponent } from './discounts/discount-detail/discount-detail.component';
import { DiscountFormComponent } from './discounts/discount-form/discount-form.component';
import { DiscountListComponent } from './discounts/discount-list/discount-list.component';
import { DiscountSummaryComponent } from './discounts/discount-summary/discount-summary.component';
import { EditDiscountsV2Component } from './discounts/edit-discounts-v2/edit-discounts-v2.component';
import { EditDiscountsEffects } from './discounts/edit-discounts-v2/state/edit-discounts.effects';
import { editDiscountsReducer } from './discounts/edit-discounts-v2/state/edit-discounts.reducer';
import { EditableStepComponent } from './discounts/editable-step/editable-step.component';
import { QuickFiltersComponent } from './discounts/quick-filters/quick-filters.component';
import { DateFilterMenuComponent } from './discounts/quick-filters/date-filter-menu/date-filter-menu.component';
import { StatusFilterMenuComponent } from './discounts/quick-filters/status-filter-menu/status-filter-menu.component';
import { SaleItemModalComponent } from './discounts/sale-item-modal/sale-item-modal.component';
import { NotFoundComponent } from './error/not-found/not-found.component';
import { UnknownErrorComponent } from './error/unknown-error/unknown-error.component';
import { ExternalLinkComponent } from './external-link/external-link.component';
import { CsvExportModule } from './modules/csv-export/csv-export.module';
import { baseHrefProvider } from './providers/base-href.provider';
import { SeFeTranslateLoader } from './providers/se-fe-translate.loader';
import { routes } from './routes';
import { ItemDetailComponent } from './sale-items/item-detail/item-detail.component';
import { ItemListComponent } from './sale-items/item-list/item-list.component';
import { ItemVariationDetailComponent } from './sale-items/item-variation-detail/item-variation-detail.component';
import { RegistrationWarningModalComponent } from './sale-items/registration-warning-modal/registration-warning-modal.component';
import { AppErrorHandlerService } from './services/app-error-handler.service';
import { DatadogService } from './services/datadog.service';
import { AppExternalLinkService } from './services/external-link.service';
import { LoadScriptsService } from './services/load-scripts.service';
import { SvgSpriteComponent } from './svg-sprite/svg-sprite.component';
import { A11yModule } from '@angular/cdk/a11y';
import { SeFeLinkModule } from 'se-fe-link';

export const SE_FE_OVERLAY_PARENT_REF = '__seFeOverlayParent__';

type SeFeOverlayEvent = CustomEvent<{
  element?: (HTMLElement & { [SE_FE_OVERLAY_PARENT_REF]?: HTMLElement }) | null;
}>;

let frameRouterLocalModalOverlayFilterRegistered = false;

// Keep iframe-local modals out of frame-router full-screen mode; it hides the HQ shell.
export const FRAME_ROUTER_OVERLAY_SELECTOR = '.pl-pane--full-screen-no-nav';

export function stopFrameRouterLocalModalOverlay(event: Event): void {
  const overlayParent = (event as SeFeOverlayEvent).detail?.element?.[SE_FE_OVERLAY_PARENT_REF];

  if (overlayParent?.tagName === 'SE-FE-MODAL') {
    event.stopImmediatePropagation();
  }
}

export function registerFrameRouterLocalModalOverlayFilter(): void {
  if (frameRouterLocalModalOverlayFilterRegistered || typeof document === 'undefined') {
    return;
  }

  document.addEventListener('seFeOverlay:open', stopFrameRouterLocalModalOverlay, true);
  document.addEventListener('seFeOverlay:close', stopFrameRouterLocalModalOverlay, true);
  frameRouterLocalModalOverlayFilterRegistered = true;
}

@NgModule({
  exports: [RouterModule, TranslatePipe, TranslateDirective],
  declarations: [
    AddDiscountComponent,
    AppComponent,
    CustomerBuysComponent,
    CustomerGetsComponent,
    DateFilterMenuComponent,
    DiscountDetailComponent,
    DiscountFormComponent,
    DiscountListComponent,
    DiscountSummaryComponent,
    EditDiscountsV2Component,
    EditableStepComponent,
    ExternalLinkComponent,
    ItemDetailComponent,
    ItemListComponent,
    ItemVariationDetailComponent,
    NotFoundComponent,
    QuickFiltersComponent,
    RegistrationWarningModalComponent,
    SaleItemModalComponent,
    StatusFilterMenuComponent,
    SvgSpriteComponent,
    UnknownErrorComponent,
  ],
  bootstrap: [AppComponent],
  // This is needed for custom elements that are not registered Angular components in our application.
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    FormsModule,
    SeFeApiModule.forRoot({
      domainEnvironments: environment.domainEnvironments,
    }),
    ReactiveFormsModule,
    RouterModule.forRoot(routes, {
      // v22 defaults to 'always'. Keep the v21 emptyOnly merge for params/data.
      paramsInheritanceStrategy: 'emptyOnly',
    }),
    SeAnalyticsModule.forRoot({
      platProp: 'se_store_ui',
      platformSubSec: 'HQ',
      currentEnv: environment.name,
    }),
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeChipModule,
    SeFeConfirmModule,
    SeFeDatepickerModule,
    SeFeEmptyStateModule,
    SeFeFilterChipsModule,
    SeFeFormControlHelpModule,
    SeFeFormFieldModule,
    SeFeFormFieldOptionsModule,
    SeFeFormFieldTextModule,
    SeFeFrameRouterModule.forRoot({
      origin: environment.seFeFrameRouterOrigin,
      redirectUrl: environment.seFeFrameRouterRedirectUrl,
      overlaySelector: FRAME_ROUTER_OVERLAY_SELECTOR,
      overlayDelay: 300,
    }),
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeIconModule.include([
      seFeIconArchive,
      seFeIconChevronLeft,
      seFeIconChevronRight,
      seFeIconClipboardThin,
      seFeIconDiscountThin,
      seFeIconDotFill,
      seFeIconPriceTagThin,
      seFeIconPencilFill,
      seFeIconOpenInNew,
      seFeIconRemove,
      seFeIconSearch,
      seFeIconSearchThin,
      seFeIconSpotDiscount,
      seFeIconTrash,
      seFeIconUnarchive,
      seFeIconWarning,
      seFeIconSpotClipboard,
      seFeIconPlusCircle,
    ]),
    SeFeLinkModule,
    SeFeMenuModule,
    SeFeModalModule,
    SeFePageModule,
    SeFePaginationModule,
    SeFePositionModule,
    SeFeProgressBarModule,
    SeFeScrollModule,
    SeFeServiceModule.forRoot({
      domainEnvironments: environment.domainEnvironments,
    }),
    SeFeSpinnerModule,
    SeFeTableModule,
    SeFeTabGroupModule,
    SeFeTestIdModule,
    SeFeToastModule,
    SeFeToolbarModule,
    SeFeTooltipModule,
    TranslatePipe,
    TranslateDirective,
    SeFeFormFieldSuffixModule,
    StoreModule.forRoot({
      editDiscounts: editDiscountsReducer,
      router: routerReducer,
    }),
    EffectsModule.forRoot([EditDiscountsEffects]),
    StoreDevtoolsModule.instrument({ maxAge: 25 }),
    StoreRouterConnectingModule.forRoot(),
    A11yModule,
  ],
  providers: [
    AppExternalLinkService,
    baseHrefProvider,
    CsvExportModule,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    LoadScriptsService,
    DatadogService,
    { provide: ErrorHandler, useClass: AppErrorHandlerService },
    { provide: SeFeFrameRouterRedirectService, useClass: AppRedirectService },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideTranslateService({
      lang: 'en',
      fallbackLang: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: SeFeTranslateLoader,
      },
    }),
    SeFeTranslationsLoader,
    { provide: SE_FE_TRANSLATIONS_LANGUAGES, useValue: ['en'] },
    { provide: SE_FE_TRANSLATIONS_BASE_PATH, useValue: '' },
    { provide: SE_FE_TRANSLATIONS_PREFIX, useValue: 'assets/translations' },
  ],
})
export class AppModule {
  constructor() {
    registerFrameRouterLocalModalOverlayFilter();
  }
}
